// Treemap — weighted treemap of blocks (area ∝ value), built on Recharts'
// <Treemap> (it owns the tiling). Generic: each item supplies its own
// value/color + display strings; the hover tooltip body comes from
// renderTip(item). Tiles are custom-rendered via the `content` render-prop to
// reproduce the design's rect + sheen + tiered labels; hover reuses ChartTooltip
// so it matches the other charts. Theming is token-driven (text via var(--…),
// tile fills from the item color).
import { useState } from 'react';
import PropTypes from 'prop-types';
import { Treemap as RTreemap, ResponsiveContainer } from 'recharts';
import { isLightColor, truncate } from '../../../utils/treemap';
import { ChartTooltip } from './ChartTooltip';
import { useTipHold } from './useTipHold';
import './treemap.css';

const GAP = 2; // px inset between tiles (reveals the panel behind → separated cards)
const R = 8; // tile corner radius

// Custom node renderer. Recharts gives geometry in real pixels, so label tiers
// are chosen by the tile's pixel size (not just its %) and only what fits is
// drawn — names are truncated to width and tiny tiles drop text to avoid the
// overlap/overflow you get from fixed font sizes. Only leaf tiles (depth 1).
function Tile(props) {
  const { x, y, width, height, depth, label, name, sub, value, pctLabel, color, onHover, onPick } = props;
  if (depth !== 1 || color == null) return null;
  const shown = pctLabel ?? `${value}%`;

  // Inset so tiles don't touch; bail on slivers too small to show after the gap.
  const ix = x + GAP / 2;
  const iy = y + GAP / 2;
  const iw = Math.max(0, width - GAP);
  const ih = Math.max(0, height - GAP);
  if (iw <= 0 || ih <= 0) return null;

  // Tier by actual pixel size so labels always fit the rendered tile.
  const big = iw >= 150 && ih >= 108;
  const medium = !big && iw >= 88 && ih >= 60;
  const small = !big && !medium && iw >= 46 && ih >= 30;

  const light = isLightColor(color);
  const textColor = light ? '#0a1a3a' : '#ffffff';
  const subColor = light ? 'rgba(10,26,58,0.65)' : 'rgba(255,255,255,0.72)';
  const nameChars = Math.max(4, Math.floor((iw - 28) / 8));

  return (
    <g
      className="treemap-tile"
      onMouseEnter={() => onHover && onHover(props)}
      onMouseLeave={() => onHover && onHover(null)}
      onClick={() => onPick && onPick(props)}
    >
      <rect x={ix} y={iy} width={iw} height={ih} rx={R} ry={R} fill={color} />
      <rect x={ix} y={iy} width={iw} height={ih / 2} rx={R} ry={R} fill="url(#treemap-sheen)" pointerEvents="none" />
      {big && (
        <g pointerEvents="none">
          <text x={ix + 14} y={iy + 22} fill={subColor} fontFamily="Plus Jakarta Sans" fontWeight="700" fontSize="10" letterSpacing="1.4">{label}</text>
          <text x={ix + 14} y={iy + 40} fill={textColor} fontFamily="Plus Jakarta Sans" fontWeight="700" fontSize="14" letterSpacing="-0.2">{truncate(name || '', nameChars)}</text>
          <text x={ix + 14} y={iy + ih - 24} fill={textColor} fontFamily="Plus Jakarta Sans" fontWeight="700" fontSize="22" letterSpacing="-0.4">{shown}</text>
          <text x={ix + 14} y={iy + ih - 9} fill={subColor} fontFamily="Inter" fontWeight="600" fontSize="11">{sub}</text>
        </g>
      )}
      {medium && (
        <g pointerEvents="none">
          <text x={ix + 12} y={iy + 18} fill={subColor} fontFamily="Plus Jakarta Sans" fontWeight="700" fontSize="9" letterSpacing="1.2">{label}</text>
          <text x={ix + 12} y={iy + ih - 20} fill={textColor} fontFamily="Plus Jakarta Sans" fontWeight="700" fontSize="15" letterSpacing="-0.3">{shown}</text>
          <text x={ix + 12} y={iy + ih - 7} fill={subColor} fontFamily="Inter" fontWeight="600" fontSize="10">{sub}</text>
        </g>
      )}
      {small && (
        <g pointerEvents="none">
          <text x={ix + iw / 2} y={iy + ih / 2 - 3} textAnchor="middle" fill={textColor} fontFamily="Plus Jakarta Sans" fontWeight="700" fontSize="10" letterSpacing="0.3">{label}</text>
          <text x={ix + iw / 2} y={iy + ih / 2 + 11} textAnchor="middle" fill={subColor} fontFamily="Plus Jakarta Sans" fontWeight="700" fontSize="10">{shown}</text>
        </g>
      )}
    </g>
  );
}

export function Treemap({ items, onSelect, renderTip, interactiveTip = false, ariaLabel = 'Treemap' }) {
  const [hoverKey, setHoverKey] = useState(null);

  // `id` (not `key`, which React reserves) carries identity through Recharts.
  const data = items.map((it) => ({
    id: it.key,
    value: it.value,
    pctLabel: it.pctLabel,
    color: it.color,
    label: it.label,
    name: it.name,
    sub: it.sub,
  }));

  const hovered = hoverKey ? items.find((it) => it.key === hoverKey) : null;
  const clearHover = () => setHoverKey(null);
  // Same grace period the bar chart uses, for the same reason: with a control in the
  // card, leaving a tile must not close the card the pointer is on its way to.
  const { scheduleClose, cancelClose, closeNow } = useTipHold(clearHover, interactiveTip && !!renderTip);

  // Only identity is recorded — ChartTooltip places itself beside the pointer, so
  // the tile's geometry isn't needed (it used to anchor the card over the tile).
  const onHover = (node) => {
    if (node) cancelClose();
    if (node) setHoverKey(node.id);
    else scheduleClose();
  };

  // Leaving the map clears the hover as a backstop — a fast exit can skip a tile's own
  // mouseleave, and the card is `position: fixed`, so a stale one floats over the page.
  return (
    <div className="treemap" onMouseLeave={scheduleClose}>
      <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
        <defs>
          <linearGradient id="treemap-sheen" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.12" />
            <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>
      <ResponsiveContainer width="100%" height="100%">
        <RTreemap
          data={data}
          dataKey="value"
          aspectRatio={4 / 3}
          stroke="none"
          isAnimationActive={false}
          content={<Tile
            onHover={onHover}
            onPick={(node) => {
              // Resolve the tile's source item (id === item.key) and pass the full
              // item so the contract matches renderTip(item).
              const item = items.find((x) => x.key === node.id);
              if (item && onSelect) onSelect(item);
            }}
          />}
          aria-label={ariaLabel}
        />
      </ResponsiveContainer>
      {renderTip && (
        <ChartTooltip
          visible={!!hovered}
          onDismiss={closeNow}
          interactive={interactiveTip}
          onHold={cancelClose}
          onRelease={scheduleClose}
        >
          {hovered ? renderTip(hovered) : null}
        </ChartTooltip>
      )}
    </div>
  );
}

Treemap.propTypes = {
  /** Blocks to lay out. value drives area + the displayed %; label/name/sub are text. */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      /** Displayed % when the area is floored away from the real share; defaults to `${value}%`. */
      pctLabel: PropTypes.string,
      color: PropTypes.string.isRequired,
      label: PropTypes.string,
      name: PropTypes.string,
      sub: PropTypes.string,
    }),
  ).isRequired,
  onSelect: PropTypes.func,
  /** (item) => node — tooltip body shown on hover. */
  renderTip: PropTypes.func,
  /** The tooltip holds a control — see BarChart's prop of the same name. */
  interactiveTip: PropTypes.bool,
  ariaLabel: PropTypes.string,
};

export default Treemap;
